import { useEffect, useMemo, useState } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import useLanguage from '@op/hooks/useLanguage';
import { getSummaryEvent } from '@op/services/keyMetrics';
import { DBO_TM } from '@op/utils/format';

export interface ISummaryItem {
  title: string;
  count?: number;
  sentiment_type: string;
  clickable: boolean;
  cluster_id?: string;
}

interface IUseSummaryEventParams {
  filter?: any;
  gameInfo?: any;
  channelType?: string;
  sentimentType?: string;
  isSteam?: boolean;
}

export default function useSummaryEvent({
  filter,
  gameInfo,
  channelType = 'total',
  sentimentType = 'total',
  isSteam = false,
}: IUseSummaryEventParams) {
  const { isEn } = useLanguage();
  const { loading, wrapperRequest } = useCancelRequestHook();
  const [summaryData, setSummaryData] = useState<any>(null);

  const requestSummaryData = () => {
    const payload: any = {
      start_time: filter.start_time,
      end_time: filter.end_time,
      date_type: filter.date_type,
      sentiment_type: sentimentType,
      channel_type: channelType,
      page_source: 'pc_opinion',
      ...gameInfo,
      ...(isSteam ? { has_steam_conf: true } : {}),
    };
    wrapperRequest((cancelFunc: any) =>
      getSummaryEvent(payload, cancelFunc),
    )
      .then((res: any) => {
        setSummaryData(res);
      })
      .catch(() => {
        setSummaryData(null);
      });
  };

  useEffect(() => {
    if (!filter || !gameInfo) return;
    requestSummaryData();
  }, [filter, gameInfo, channelType, sentimentType, isSteam]);

  const hasEventStats = summaryData?.is_v2 === true;

  // Normalize both V2 and V1 data into the same ISummaryItem[] per sentiment
  const sentimentGroups = useMemo((): Record<string, ISummaryItem[]> => {
    if (!summaryData) return { positive: [], negative: [], neutral: [] };

    if (hasEventStats) {
      const eventList = summaryData?.event_stats?.list || {};
      const result: Record<string, ISummaryItem[]> = {
        positive: [],
        negative: [],
        neutral: [],
      };

      (['positive', 'negative', 'neutral'] as const).forEach(sentiment => {
        result[sentiment] = (eventList[sentiment] || []).map((e: any) => ({
          title: isEn
            ? e.event_title_en || e.event_title
            : e.event_title_zh || e.event_title,
          count: e.count ?? e.comment_count ?? 0,
          sentiment_type: sentiment,
          clickable: true,
          cluster_id: e.cluster_id,
        }));
      });

      return result;
    }

    // V1: report_stats
    const reportStats = summaryData?.report_stats || {};
    const reportData = isEn ? reportStats.report_en : reportStats.report_cn;
    const result: Record<string, ISummaryItem[]> = {
      positive: [],
      negative: [],
      neutral: [],
    };

    (['positive', 'negative', 'neutral'] as const).forEach(sentiment => {
      result[sentiment] = ((reportData?.[sentiment] || []) as string[]).map(
        (text: string) => ({
          title: text,
          sentiment_type: sentiment,
          clickable: false,
        }),
      );
    });

    return result;
  }, [summaryData, isEn, hasEventStats]);

  const hasData = useMemo(() => {
    if (loading) return false;
    return Object.values(sentimentGroups).some(items => items.length > 0);
  }, [loading, sentimentGroups]);

  const tipsMap = useMemo(() => {
    if (
      summaryData?.is_today === false &&
      summaryData?.data_date &&
      !hasEventStats
    ) {
      return {
        zh: `暂无今日总结，已自动切换至最新(${DBO_TM.sFormat(
          summaryData?.data_date,
        )})的总结内容。`,
        en: `No summary available for today. Automatically switched to latest summary (${DBO_TM.sFormat(
          summaryData?.data_date,
        )}).`,
      };
    }
    return undefined;
  }, [summaryData]);

  return {
    loading,
    hasEventStats,
    sentimentGroups,
    hasData,
    summaryData,
    tipsMap,
  };
}
